//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState, useEffect } from 'react';
import { makeStyles, MenuItem, /*RadioGroup, FormControlLabel, Radio,*/ Grid } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Divider from '@material-ui/core/Divider';
import { useTheme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    JOB_TYPE_FILTER_OPTIONS,
    FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES,
    // SALARY_FILTER_OPTIONS,
    // PAY_FREQUENCY_FILTER_OPTIONS,
    // getRateFilterOptions,
    ALL,
    // FILTER_OPTION_RATE_ANY_RATE,
    // DATE_POSTED_FILTER_OPTIONS,
    // FILTER_OPTION_PAY_FREQUENCY_ANY_FREQUENCY,
    // FILTER_OPTION_SALARY_ANY_SALARY,
    MESSAGE_TITLE_SALARY_RATE_INCOMPLETE,
    MESSAGE_SALARY_RATE_INCOMPLETE,
} from '../../common/constants';
import { updateRecentSearches } from '../../common/services/talentCenterServices';
import { getUniqueKey } from '../../common/arrayHelper';
import { getNewTalentCenterQuery } from '../../common/queryHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdIconButton from '../../common/elemental/AfitdIconButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdSalaryRateFilter from '../../common/elemental/AfitdSalaryRateFilter';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

import Menu from '../../common/elemental/AfitdMenu';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import CheckBox from './CheckBox';
import SearchSaveItem from './SearchSaveItem';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _size from 'lodash/size';
import _filter from 'lodash/filter';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        filterBar: {
            // display: 'flex',
            borderRadius: '4px',
            // width: '100%',
            justifyContent: 'flex-end',
            alignItems: 'center',
            // flexDirection: 'row',
            // flexWrap: 'wrap',
            [theme.breakpoints.down('md')]: { justifyContent: 'center' },
            [theme.breakpoints.up('xl')]: { width: '100%' },
            [theme.breakpoints.only('lg')]: { paddingLeft: 13 },
            [theme.breakpoints.up('md')]: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap' },
        },
        menuItem: {
            whiteSpace: 'normal',
            '& .MuiCheckbox-root, & .MuiRadio-root': {
                paddingTop: 0,
                paddingBottom: 0,
            },
        },
        jobTypeMenuItem: {
            whiteSpace: 'nowrap',
            '& .MuiCheckbox-root, & .MuiRadio-root': {
                paddingTop: 0,
                paddingBottom: 0,
            },
        },
        radioItem: {
            '& label': {
                marginBottom: 5,
            },
        },
        filterItem: {
            fontSize: '1rem',
            fontWeight: '500',
            // marginRight: '20px',
            // maxWidth: '200px',
            [theme.breakpoints.up('md')]: { marginRight: '20px', maxWidth: '212px' },
            [theme.breakpoints.only('md')]: { marginRight: '4px' },
            [theme.breakpoints.down('sm')]: { background: 'white', borderRadius: 8, padding: 16, margin: 16 },
        },
        '@media (max-width: 800px)': {
            filterBar: {
                justifyContent: 'flex-start',
            },
        },

        recentSearchDropDown: {
            maxWidth: '200px',
            color: '#000',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
        },

        clearRecentSearchesButton: {
            width: '100%',
            color: '#1D8A8B',
        },
        resultButton: { width: '100%', marginBottom: 16 },
        resultButtonContainer: { margin: '0px 16px 16px 16px' },
        filterTitle: { fontSize: 16, fontWeight: 'bold', margin: '21px 16px 16px 16px' },
        bottomDivider: {
            marginBottom: 16,
        },
        navigation: { padding: '0px 16px' },
        navigationIconContainer: {
            width: '32px',
            height: '32px',
            background: '#F2F2F4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': { cursor: 'pointer' },
        },
        navIconLeft: { marginLeft: 11, width: 18 },
        navigationText: { marginLeft: 8, fontSize: 16, fontWeight: 'bold', '&:hover': { cursor: 'pointer' }, marginTop: '-2px' },
        filterHeaderContainer2: { display: 'flex', justifyContent: 'center' },
        filterHeaderContainer3: { display: 'flex', justifyContent: 'flex-end', paddingRight: 5 },
        filterHeaderText: { fontSize: '15px', fontWeight: 600 },
        resetText: { fontSize: '15px', fontWeight: 600 },
        headerDivider: { paddingTop: 16 },
        IconButtonContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '5px',
        },
        CloseIconButton: {
            padding: '5px',
        },
        CloseIcon: {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '18px',
        },
        SaveIconButton: {
            padding: '5px',
            marginRight: '10px',
        },
        SaveIcon: {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '18px',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        appContext,
        smMatches,
        willingRelocate,
        handleChangeWillingRelocate,
        handleChangeStandaloneWillingRelocate,
        salaryRateIsValid,
        setSalaryRateIsValid,
        smViewportSalaryRate,
        handleSubmitSalaryRate,
        handleClearSalaryRate,
        setIsMenuClosed,
        // setSelectedJobTypes,
        salaryRateIncompleteMessageDialogOpen,
        handleCloseSalaryRateIncompleteMessageDialogOpen,
        handleClickReset,
    } = params;

    const { query = {}, setQuery } = props;
    const willingRelocateInQuery = _get(query, 'willingRelocate');
    const salaryRateInQuery = _get(query, 'salaryRate');
    const isExactRateInQuery = _get(salaryRateInQuery, 'isExactRate', null);
    const rateMinInQuery = _get(salaryRateInQuery, 'rateMin', '');
    const rateMaxInQuery = _get(salaryRateInQuery, 'rateMax', '');
    const payFrequencyInQuery = _get(salaryRateInQuery, 'payFrequency', null);

    const isExactRateSmViewport = _get(smViewportSalaryRate, 'isExactRate', null);
    const rateMinSmViewport = _get(smViewportSalaryRate, 'rateMin', '');
    const rateMaxSmViewport = _get(smViewportSalaryRate, 'rateMax', '');
    const payFrequencySmViewport = _get(smViewportSalaryRate, 'payFrequency', null);
    // const currencySymbolSmViewport = _get(smViewportSalaryRate, 'currencySymbol', null);

    const { userId } = appContext;

    const defns = {
        navigation: { container: true, alignItems: 'center' },
        navigationItem: { item: true, xl: 4, lg: 4, md: 4, sm: 4, xs: 4 },
        filterHeaderContainer2: { item: true, xl: 4, lg: 4, md: 4, sm: 4, xs: 4 },
        filterHeaderContainer3: { item: true, xl: 4, lg: 4, md: 4, sm: 4, xs: 4 },
        recentSearchDropDown: {
            closeable: true,
            label: 'Recent searches',
            name: 'recentSearches',
        },
        willingRelocate: {
            label: 'Willing to Relocate',
            onChange: handleChangeWillingRelocate,
            checked: Boolean(willingRelocate),
        },
        standaloneWillingRelocate: {
            label: 'Willing to Relocate',
            onChange: handleChangeStandaloneWillingRelocate,
            checked: Boolean(willingRelocateInQuery),
        },
        jobTypeDropDown: {
            label: 'Job Type',
            name: 'jobTypes',
            onClick: () => setIsMenuClosed(false),
        },
        rateRadio: {
            value: query.rate,
            'aria-label': 'Rate',
            name: 'rate',
            onChange: e => {
                let val = e.target.value;
                setQuery(prev => ({ ...prev, rate: val }));
            },
        },
        salaryDropDown: {
            label: 'Desired Salary / Rate',
            name: 'salary',
            onClick: () => setIsMenuClosed(false),
        },
        salaryRate: {
            variant: 'single-max',
            isExactRate: smMatches ? isExactRateSmViewport : isExactRateInQuery,
            rateMin: smMatches ? rateMinSmViewport : rateMinInQuery,
            rateMax: smMatches ? rateMaxSmViewport : rateMaxInQuery,
            payFrequency: smMatches ? payFrequencySmViewport : payFrequencyInQuery,
            onSubmit: handleSubmitSalaryRate,
            onClear: handleClearSalaryRate,
            maxValue: MAX_VALUE,
            setIsValid: setSalaryRateIsValid,
            isValid: salaryRateIsValid,
        },
        clearRecentSearchesButton: {
            onClick: () => {
                updateRecentSearches({ userId, recentSearches: [] });
            },
        },
        recentSearchPlaceholder: {
            style: {
                textAlign: 'center',
            },
            color: 'secondary',
            variant: 'subtitle1',
        },
        resultButton: { color: 'primary' },
        resetText: {
            onClick: handleClickReset,
        },
        salaryRateIncompleteMessageDialog: {
            title: MESSAGE_TITLE_SALARY_RATE_INCOMPLETE,
            open: salaryRateIncompleteMessageDialogOpen,
            message: MESSAGE_SALARY_RATE_INCOMPLETE,
            onClose: handleCloseSalaryRateIncompleteMessageDialogOpen,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const MAX_VALUE = 1000000;

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const FilterBar = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles()();

    const appContext = useContext(AppContext);

    const layoutContext = useContext(LayoutContext);
    const { recentSearches } = layoutContext;

    const theme = useTheme();
    const lgMatches = useMediaQuery(theme.breakpoints.only('lg'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { query, setQuery, setShowFilterPage, setShowAds, queryInitState } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [salaryRateIsValid, setSalaryRateIsValid] = useState(true);
    const [willingRelocate, setWillingRelocate] = useState();
    const [selectedJobTypes, setSelectedJobTypes] = useState([]);
    const [isMenuClosed, setIsMenuClosed] = useState(false);
    const [smViewportSalaryRate, setSmViewportSalaryRate] = useState(null);
    const [salaryRateIncompleteMessageDialogOpen, setSalaryRateIncompleteMessageDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        const willingRelocateInQuery = query?.willingRelocate;
        const jobTypesInQuery = query?.jobType;
        const salaryRateInQuery = query?.salaryRate;

        // sync work from home
        setWillingRelocate(willingRelocateInQuery);

        // sync job types
        // if not all selected, remove 'All Job Types'; otherwise, sync query with checkboxes
        if (_size(JOB_TYPE_FILTER_OPTIONS) !== _size(jobTypesInQuery)) setSelectedJobTypes(_filter(jobTypesInQuery, el => el !== FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES));
        else setSelectedJobTypes(jobTypesInQuery);

        // sync salary/rate (for 360px viewport)
        setSmViewportSalaryRate(salaryRateInQuery);
    }, [query]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    // handle stand-alone willing to relocate, which is ready to submit query
    const handleChangeStandaloneWillingRelocate = val => {
        setQuery(prevQuery =>
            prevQuery?.willingRelocate
                ? getNewTalentCenterQuery({ prevQuery, newQuery: { ...prevQuery }, omit: ['willingRelocate'] })
                : getNewTalentCenterQuery({ prevQuery, newQuery: { willingRelocate: true } }),
        );
    };

    // handle salaryRate onSubmit
    const handleSubmitSalaryRate = ({ isExactRate, rateMin, rateMax, payFrequency, currencySymbol, showOnly } = {}) => {
        if (!rateMin && !payFrequency) setQuery(prevQuery => getNewTalentCenterQuery({ prevQuery, newQuery: { salaryRate: null } }));
        else
            setQuery(prevQuery =>
                getNewTalentCenterQuery({ prevQuery, newQuery: { salaryRate: { rateMin: rateMin || 0, rateMax, payFrequency, currencySymbol, showOnly /* isExactRate */ } } }),
            );
        setIsMenuClosed(true);
    };

    // handle salaryRate onSubmit on 360px viewport (onChange events)
    const handleChangeSalaryRate = ({ isExactRate, rateMin, rateMax, payFrequency, currencySymbol, showOnly } = {}) => {
        if ((rateMax && payFrequency) || (!rateMax && !payFrequency)) {
            setSalaryRateIncompleteMessageDialogOpen(false);
            setSalaryRateIsValid(true);
        }

        setSmViewportSalaryRate(prev => ({
            ...prev,
            rateMin,
            rateMax,
            payFrequency,
            showOnly,
        }));
    };

    // handle salaryRate onClear
    const handleClearSalaryRate = () => {
        setQuery(prevQuery => getNewTalentCenterQuery({ prevQuery, newQuery: { salaryRate: null } }));
        setIsMenuClosed(true);
    };

    // handle willing to relocate onChange
    const handleChangeWillingRelocate = val => {
        setWillingRelocate(prev => !prev);
    };

    const handleChangeJobType = val => {
        // if already in the list, user is deselecting
        const isDeselect = selectedJobTypes.includes(val);

        // if 'All Job Types'
        if (val === FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES) setSelectedJobTypes(isDeselect ? [] : JOB_TYPE_FILTER_OPTIONS);
        else {
            // individual job type (if deselecting, remove that along 'All Job Types')
            isDeselect
                ? setSelectedJobTypes(_filter(selectedJobTypes, el => el !== val && el !== FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES))
                : setSelectedJobTypes([...selectedJobTypes, val]);
        }
    };

    // query on job type
    const handleSubmitJobType = () => {
        setQuery(prevQuery => getNewTalentCenterQuery({ prevQuery, newQuery: { jobType: selectedJobTypes } }));
        setIsMenuClosed(true);
    };

    // clearing out job types
    const handleClearJobType = () => {
        setSelectedJobTypes([]);
        setQuery(prevQuery => getNewTalentCenterQuery({ prevQuery, newQuery: { jobType: [] } }));
        setIsMenuClosed(true);
    };

    // query in phone screen (360px viewport)
    const handleClickShowResultButton = async () => {
        // validate Salary/Rate
        // const rateMin = smViewportSalaryRate?.rateMin;
        const rateMax = smViewportSalaryRate?.rateMax;
        const payFrequency = smViewportSalaryRate?.payFrequency;
        // const showOnly = smViewportSalaryRate?.showOnly;
        if ((rateMax && !payFrequency) || (!rateMax && payFrequency)) {
            setSalaryRateIncompleteMessageDialogOpen(true);
            setSalaryRateIsValid(false);
        } else {
            const queryDto = { willingRelocate: true, jobType: selectedJobTypes, salaryRate: smViewportSalaryRate };
            setQuery(prevQuery => {
                const newQuery = !!willingRelocate
                    ? getNewTalentCenterQuery({ prevQuery, newQuery: queryDto })
                    : getNewTalentCenterQuery({ prevQuery, newQuery: queryDto, omit: ['willingRelocate'] });
                return newQuery;
            });
            setShowAds(false);
            setShowFilterPage(false);
        }
    };

    const handleCloseSalaryRateIncompleteMessageDialogOpen = () => {
        setSalaryRateIncompleteMessageDialogOpen(false);
    };

    const handleClickReset = () => {
        setSalaryRateIsValid(true);
        setSmViewportSalaryRate(null);
        setQuery(queryInitState);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        appContext,
        smMatches,
        willingRelocate,
        handleChangeWillingRelocate,
        handleChangeStandaloneWillingRelocate,
        salaryRateIsValid,
        setSalaryRateIsValid,
        smViewportSalaryRate,
        handleSubmitSalaryRate,
        handleClearSalaryRate,
        setIsMenuClosed,
        setSelectedJobTypes,
        salaryRateIncompleteMessageDialogOpen,
        handleCloseSalaryRateIncompleteMessageDialogOpen,
        handleClickReset,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            {smMatches ? (
                <div {...defns.filterBar}>
                    <Grid {...defns.navigation}>
                        <Grid {...defns.navigationItem}>
                            <div {...defns.navigationIconContainer} onClick={() => setShowFilterPage(false)}>
                                <ArrowBackIosIcon {...defns.navIconLeft} />
                            </div>
                        </Grid>
                        <Grid {...defns.filterHeaderContainer2}>
                            <p {...defns.filterHeaderText}>Filter</p>
                        </Grid>
                        <Grid {...defns.filterHeaderContainer3}>
                            <p {...defns.resetText} style={{ cursor: 'pointer' }}>
                                Reset
                            </p>
                        </Grid>
                    </Grid>
                    <div {...defns.headerDivider}>
                        <Divider />
                    </div>
                    <div {...defns.filterItem} style={{ padding: '8px 8px 8px 16px' }}>
                        <CheckBox {...defns.willingRelocate} />
                    </div>
                    <p {...defns.filterTitle}>Job Type</p>
                    <div {...defns.filterItem}>
                        {JOB_TYPE_FILTER_OPTIONS.map(el => {
                            const label = el === FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES ? ALL : el;
                            return (
                                <p {...defns.menuItem} key={getUniqueKey()}>
                                    <CheckBox label={label} checked={selectedJobTypes.includes(el)} onChange={handleChangeJobType} />
                                </p>
                            );
                        })}
                    </div>
                    <p {...defns.filterTitle}>Desired Salary / Rate</p>
                    <div {...defns.filterItem}>
                        <AfitdSalaryRateFilter {...defns.salaryRate} isButtonHidden={true} onSubmit={handleChangeSalaryRate} />
                    </div>
                    <div {...defns.bottomDivider}>
                        <Divider />
                    </div>
                    <div {...defns.resultButtonContainer}>
                        <AfitdButton {...defns.resultButton} onClick={handleClickShowResultButton}>
                            Show Results
                        </AfitdButton>
                    </div>
                </div>
            ) : (
                <div {...defns.filterBar}>
                    {!lgMatches ? null : (
                        <div {...defns.filterItem}>
                            <Menu {...defns.recentSearchDropDown}>
                                {recentSearches.map(el => (
                                    <MenuItem {...defns.menuItem} key={getUniqueKey()}>
                                        <SearchSaveItem onClick={() => setQuery(el)} {...el} />
                                    </MenuItem>
                                ))}
                                {recentSearches.length ? (
                                    <AfitdButton {...defns.clearRecentSearchesButton}>Clear</AfitdButton>
                                ) : (
                                    <div>
                                        <AfitdTypography {...defns.recentSearchPlaceholder}>No Recent Searches</AfitdTypography>
                                    </div>
                                )}
                            </Menu>
                        </div>
                    )}
                    <div {...defns.filterItem}>
                        <CheckBox {...defns.standaloneWillingRelocate} />
                    </div>
                    <div {...defns.filterItem}>
                        <Menu {...defns.jobTypeDropDown} close={isMenuClosed}>
                            {JOB_TYPE_FILTER_OPTIONS.map(el => {
                                return (
                                    <MenuItem {...defns.jobTypeMenuItem} key={getUniqueKey()}>
                                        <CheckBox label={el} checked={selectedJobTypes.includes(el)} onChange={handleChangeJobType} />
                                    </MenuItem>
                                );
                            })}
                            <div {...defns.IconButtonContainer}>
                                <AfitdIconButton {...defns.SaveIconButton} onClick={handleSubmitJobType}>
                                    <CheckIcon {...defns.SaveIcon} />
                                </AfitdIconButton>
                                <AfitdIconButton {...defns.CloseIconButton}>
                                    <CloseIcon {...defns.CloseIcon} onClick={handleClearJobType} />
                                </AfitdIconButton>
                            </div>
                        </Menu>
                    </div>
                    <div {...defns.filterItem}></div>
                    <div {...defns.filterItem}>
                        <Menu {...defns.salaryDropDown} close={isMenuClosed}>
                            <AfitdSalaryRateFilter {...defns.salaryRate} />
                        </Menu>
                    </div>
                    {/* <div {...defns.filterItem}>
                        <Menu {...defns.rateDropDown}>
                            <RadioGroup {...defns.rateRadio}>
                                {getRateFilterOptions().map(el => {
                                    const option = el === FILTER_OPTION_RATE_ANY_RATE ? el : `$${el.toString().replace(SALARY_FOMRAT_REGEX, ',')}+`;
                                    return (
                                        <MenuItem {...defns.menuItem} key={getUniqueKey()}>
                                            <FormControlLabel value={option} control={<Radio disableRipple />} label={option} />
                                        </MenuItem>
                                    );
                                })}
                            </RadioGroup>
                        </Menu>
                    </div> */}
                    {/* Not removing this, might need later */}
                    {/* <div {...defns.filterItem}>
                        <Menu {...defns.payFrequencyDropDown}>
                            <RadioGroup {...defns.payFrequencyRadio}>
                                {PAY_FREQUENCY_FILTER_OPTIONS.map(el => {
                                    return (
                                        <MenuItem {...defns.menuItem} key={getUniqueKey()}>
                                            <FormControlLabel value={el} control={<Radio disableRipple />} label={el} />
                                        </MenuItem>
                                    );
                                })}
                            </RadioGroup>
                        </Menu>
                    </div> */}
                </div>
            )}
            <AfitdMessageDialog {...defns.salaryRateIncompleteMessageDialog} />
        </>
    );
};

export default FilterBar;
